import React, { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Mail,
  Search,
} from 'lucide-react';
import ExportButton from '../../components/common/ExportButton';
import FilterSelect from '../../components/common/FilterSelect';
import { downloadExcelWorkbook } from '../../utils/exportExcel';
import { itemChangeLogAPI } from '../../services/api';
import { useAuthStore } from '../../store/authStore';

const normalizeText = (value) => String(value ?? '').trim();
const todayKey = () => new Date().toISOString().slice(0, 10);
const shiftDateKey = (value, days) => {
  const date = new Date(`${value}T00:00:00`);
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
};
const defaultWeekFromKey = () => shiftDateKey(todayKey(), -6);
const formatDateTime = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleString();
};
const INLINE_SELECT_CLASS_NAME = 'input min-w-0 rounded-lg border-slate-200 bg-white px-3 py-2 text-sm shadow-none focus:border-primary-500';
const CUSTOM_OPTION_VALUE = '__add_new__';
const STATUS_TONE = {
  Pending: 'bg-amber-100 text-amber-700',
  Resolved: 'bg-emerald-100 text-emerald-700',
  Escalated: 'bg-rose-100 text-rose-700',
};
const RESOLUTION_TONE = {
  Pending: 'bg-sky-100 text-sky-700',
  Resolved: 'bg-emerald-100 text-emerald-700',
  Escalated: 'bg-rose-100 text-rose-700',
};
const normalizeCompareText = (value) => normalizeText(value).toLowerCase();
const uniqueTextValues = (items = []) => Array.from(new Set(items.map((item) => normalizeText(item)).filter(Boolean)));
const buildDropdownOptions = ({
  values = [],
  currentValue = '',
  placeholder = 'Select',
  addLabel = 'Add new...',
}) => {
  const normalizedCurrent = normalizeText(currentValue);
  const entries = uniqueTextValues(values).map((value) => ({ value, label: value }));

  if (normalizedCurrent && !entries.some((entry) => normalizeCompareText(entry.value) === normalizeCompareText(normalizedCurrent))) {
    entries.unshift({ value: normalizedCurrent, label: normalizedCurrent });
  }

  return [
    { value: '', label: placeholder },
    ...entries,
    { value: CUSTOM_OPTION_VALUE, label: addLabel },
  ];
};
const promptForNewValue = (label, currentValue = '') => {
  const result = window.prompt(label, currentValue || '');
  return normalizeText(result);
};

const ItemChangeLog = () => {
  const {
    hasFeatureAccess,
    isAdmin,
    isManager,
    isAssistantManager,
    isSupervisor,
  } = useAuthStore();
  const canViewPage = hasFeatureAccess('item_change_log.page');
  const userIsAdmin = isAdmin();
  const userIsAssistantManager = isAssistantManager();
  const userIsSupervisor = isSupervisor();
  const userIsStaff = !userIsAdmin && !isManager() && !userIsAssistantManager && !userIsSupervisor;
  const isEntryStageUser = userIsSupervisor || userIsStaff;
  const canViewAssistantManagerColumns = userIsAdmin || userIsAssistantManager;
  const canViewAdminColumns = userIsAdmin;
  const canResolveEscalations = userIsAdmin || userIsAssistantManager;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingLookups, setLoadingLookups] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [options, setOptions] = useState({
    cause: [],
    action: [],
    status: [],
    resolution_status: [],
  });
  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);
  const [statusFilter, setStatusFilter] = useState('all');
  const [resolutionStatusFilter, setResolutionStatusFilter] = useState('all');
  const [dateFromFilter, setDateFromFilter] = useState(defaultWeekFromKey);
  const [dateToFilter, setDateToFilter] = useState(todayKey);
  const [selectedIds, setSelectedIds] = useState([]);
  const [saveMeta, setSaveMeta] = useState({});

  const rowsRef = useRef(rows);
  const saveTimersRef = useRef({});
  const inflightSaveRef = useRef({});
  const pendingSaveRef = useRef({});
  const rowVersionRef = useRef({});

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  const causeSuggestions = useMemo(
    () => (options.cause || []).map((option) => normalizeText(option.label || option.value)).filter(Boolean),
    [options.cause]
  );
  const actionSuggestions = useMemo(
    () => (options.action || []).map((option) => normalizeText(option.label || option.value)).filter(Boolean),
    [options.action]
  );
  useEffect(() => {
    if (!canViewPage) return undefined;

    const loadLookups = async () => {
      setLoadingLookups(true);
      try {
        const optionsRes = await itemChangeLogAPI.getOptions();

        setOptions(optionsRes.data?.data || {
          cause: [],
          action: [],
          status: [],
          resolution_status: [],
        });
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Failed to load Item Change Log lookups');
      } finally {
        setLoadingLookups(false);
      }
    };

    loadLookups();
    return undefined;
  }, [canViewPage]);

  useEffect(() => {
    if (!canViewPage) return undefined;

    const loadRows = async () => {
      setLoading(true);
      try {
        const response = await itemChangeLogAPI.getAll({
          limit: 2000,
          search: deferredSearch || undefined,
          status: statusFilter !== 'all' ? statusFilter : undefined,
          resolutionStatus: canResolveEscalations && resolutionStatusFilter !== 'all'
            ? resolutionStatusFilter
            : undefined,
          dateFrom: dateFromFilter || undefined,
          dateTo: dateToFilter || undefined,
        });

        const nextRows = response.data?.data || [];
        setRows(nextRows);
        setSelectedIds((current) => current.filter((id) => nextRows.some((row) => row.id === id)));
        setSaveMeta((current) => {
          const next = {};
          nextRows.forEach((row) => {
            if (current[row.id]) next[row.id] = current[row.id];
          });
          return next;
        });
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Failed to load Item Change Log rows');
      } finally {
        setLoading(false);
      }
    };

    loadRows();
    return undefined;
  }, [
    canResolveEscalations,
    canViewPage,
    deferredSearch,
    resolutionStatusFilter,
    dateFromFilter,
    dateToFilter,
    statusFilter,
  ]);

  useEffect(() => () => {
    Object.values(saveTimersRef.current).forEach((timer) => clearTimeout(timer));
  }, []);

  const statusOptions = useMemo(() => ([
    { value: 'all', label: 'All statuses' },
    ...(options.status || []).map((option) => ({
      value: option.label,
      label: option.label,
    })),
  ]), [options.status]);

  const resolutionOptions = useMemo(() => ([
    { value: 'all', label: 'All manager statuses' },
    ...(options.resolution_status || options.status || []).map((option) => ({
      value: option.label,
      label: option.label,
    })),
  ]), [options.resolution_status, options.status]);

  const filteredRows = useMemo(() => rows, [rows]);

  const summary = useMemo(() => {
    const resolved = filteredRows.filter((row) => row.status === 'Resolved' || row.resolutionStatus === 'Resolved').length;
    const escalated = filteredRows.filter((row) => row.status === 'Escalated' && row.resolutionStatus !== 'Resolved').length;
    const pending = filteredRows.filter((row) => row.status === 'Pending' && row.resolutionStatus !== 'Resolved').length;

    return {
      total: filteredRows.length,
      pending,
      resolved,
      escalated,
    };
  }, [filteredRows]);

  const handleDropdownOrCustom = (rowId, field, nextValue, promptLabel) => {
    if (nextValue === CUSTOM_OPTION_VALUE) {
      const customValue = promptForNewValue(promptLabel);
      if (!customValue) return;
      updateRowField(rowId, field, customValue);
      return;
    }

    updateRowField(rowId, field, nextValue);
  };

  const mergeSavedRow = (savedRow) => {
    setRows((current) => current.map((row) => (
      row.id === savedRow.id ? { ...row, ...savedRow } : row
    )));
  };

  const queueSave = (rowId, { immediate = false } = {}) => {
    clearTimeout(saveTimersRef.current[rowId]);
    const version = rowVersionRef.current[rowId] || 0;
    const delay = immediate ? 0 : 650;
    saveTimersRef.current[rowId] = setTimeout(() => {
      const activeVersion = rowVersionRef.current[rowId] || 0;
      if (activeVersion < version) return;
      void performSave(rowId, activeVersion);
    }, delay);
  };

  const performSave = async (rowId, scheduledVersion = rowVersionRef.current[rowId] || 0) => {
    if (inflightSaveRef.current[rowId]) {
      pendingSaveRef.current[rowId] = true;
      return;
    }

    const row = rowsRef.current.find((entry) => entry.id === rowId);
    if (!row) return;

    inflightSaveRef.current[rowId] = true;
    setSaveMeta((current) => ({
      ...current,
      [rowId]: {
        state: 'saving',
        timestamp: new Date().toISOString(),
      },
    }));

    try {
      const response = await itemChangeLogAPI.update(rowId, {
        sellerName: row.sellerName,
        sku: row.sku,
        productName: row.productName,
        causeText: row.causeText,
        actionText: row.actionText,
        status: row.status,
        managerActionText: row.managerActionText,
        resolutionStatus: row.resolutionStatus,
      });
      const savedRow = response.data?.data;
      if (savedRow) {
        mergeSavedRow(savedRow);
      }

      setSaveMeta((current) => ({
        ...current,
        [rowId]: {
          state: 'saved',
          timestamp: new Date().toISOString(),
        },
      }));
    } catch (error) {
      setSaveMeta((current) => ({
        ...current,
        [rowId]: {
          state: 'error',
          timestamp: new Date().toISOString(),
          message: error?.response?.data?.message || 'Failed to save row',
        },
      }));
      toast.error(error?.response?.data?.message || 'Failed to auto-save Item Change Log row');
    } finally {
      inflightSaveRef.current[rowId] = false;
      if (pendingSaveRef.current[rowId] || (rowVersionRef.current[rowId] || 0) > scheduledVersion) {
        pendingSaveRef.current[rowId] = false;
        queueSave(rowId, { immediate: true });
      }
    }
  };

  const updateRowField = (rowId, field, value) => {
    rowVersionRef.current[rowId] = (rowVersionRef.current[rowId] || 0) + 1;
    setRows((current) => current.map((row) => (
      row.id === rowId ? { ...row, [field]: value } : row
    )));
    setSaveMeta((current) => ({
      ...current,
      [rowId]: {
        state: 'editing',
        timestamp: new Date().toISOString(),
      },
    }));
    queueSave(rowId);
  };

  const toggleRowSelection = (rowId) => {
    setSelectedIds((current) => (
      current.includes(rowId)
        ? current.filter((id) => id !== rowId)
        : [...current, rowId]
    ));
  };

  const toggleAllSelection = () => {
    setSelectedIds((current) => (
      current.length === filteredRows.length
        ? []
        : filteredRows.map((row) => row.id)
    ));
  };

  const handleBulkUpdate = async (status) => {
    if (!selectedIds.length) {
      toast.error('Select at least one row first.');
      return;
    }

    setBulkSaving(true);
    try {
      const response = await itemChangeLogAPI.bulkUpdate({
        ids: selectedIds,
        status,
      });
      const updatedRows = response.data?.data || [];
      updatedRows.forEach(mergeSavedRow);
      setSelectedIds([]);

      const rowErrors = response.data?.errors || [];
      if (rowErrors.length) {
        toast.error(`Updated ${updatedRows.length} rows, but ${rowErrors.length} rows need attention.`);
      } else {
        toast.success(`${updatedRows.length} rows marked ${status.toLowerCase()}.`);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Bulk update failed');
    } finally {
      setBulkSaving(false);
    }
  };

  const exportExcel = async () => {
    const columns = [
      { header: 'Order Number', key: 'orderNumber', width: 20 },
      { header: 'Changed Date Time', key: 'changedDateTime', width: 24 },
      { header: 'Store', key: 'storeName', width: 18 },
      { header: 'Seller', key: 'sellerName', width: 20 },
      { header: 'SKU', key: 'sku', width: 16 },
      { header: 'Product', key: 'productName', width: 24 },
      { header: 'Ordered Qty', key: 'orderedQty', width: 14 },
      { header: 'Delivered Qty', key: 'deliveredQty', width: 14 },
      { header: 'Cause', key: 'causeText', width: 24 },
      { header: 'Action', key: 'actionText', width: 24 },
      { header: 'Status', key: 'status', width: 14 },
      { header: 'Escalated At', key: 'escalatedAt', width: 20 },
      { header: 'Escalated By', key: 'escalatedByName', width: 20 },
      { header: 'Assistant Manager Action', key: 'managerActionText', width: 26 },
      { header: 'Assistant Manager Status', key: 'resolutionStatus', width: 20 },
      { header: 'Admin Action', key: 'adminActionText', width: 20 },
      { header: 'Admin Status', key: 'adminStatus', width: 14 },
      { header: 'Updated By', key: 'updatedByName', width: 20 },
      { header: 'Updated At', key: 'updatedAt', width: 20 },
    ];
    const reportRows = filteredRows.map((row) => ({
      orderNumber: row.orderNumber,
      changedDateTime: row.changedDateTime,
      storeName: row.storeName,
      sellerName: row.sellerName,
      sku: row.sku,
      productName: row.productName,
      orderedQty: row.orderedQty,
      deliveredQty: row.deliveredQty,
      causeText: row.causeText,
      actionText: row.actionText,
      status: row.status,
      escalatedAt: row.escalatedAt,
      escalatedByName: row.escalatedByName,
      managerActionText: row.managerActionText || row.assistantManagerActionText || '',
      resolutionStatus: row.resolutionStatus || '',
      adminActionText: row.adminActionText || '',
      adminStatus: row.adminStatus || '',
      updatedByName: row.updatedByName,
      updatedAt: row.updatedAt,
    }));
    await downloadExcelWorkbook({
      fileName: `item-change-log-${dateFromFilter || 'from'}-to-${dateToFilter || 'to'}.xlsx`,
      sheetName: 'Item Change Log',
      columns,
      rows: reportRows,
    });
  };

  const renderSaveState = (rowId, row) => {
    const rowSave = saveMeta[rowId];
    const saveState = rowSave?.state || 'idle';

    if (saveState === 'saving') {
      return (
        <span className="inline-flex items-center gap-2 text-sky-700">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Saving...
        </span>
      );
    }

    if (saveState === 'saved') {
      return (
        <span className="inline-flex items-center gap-2 text-emerald-700">
          <CheckCircle2 className="h-3.5 w-3.5" />
          Saved {formatDateTime(rowSave?.timestamp)}
        </span>
      );
    }

    if (saveState === 'error') {
      return <span className="text-rose-700">{rowSave?.message || 'Save failed'}</span>;
    }

    return (
      <span className="text-slate-500">
        {row.updatedByName ? `${row.updatedByName} - ${formatDateTime(row.updatedAt)}` : 'Not saved yet'}
      </span>
    );
  };

  if (!canViewPage) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Item Change Log</h1>
        <div className="card">
          <p className="text-gray-500">Access denied.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1880px] space-y-5 px-5">
      <div className="flex flex-col gap-3 border-b border-slate-200 pb-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-[24px] font-bold tracking-tight text-slate-900">Item Change Log</h1>
          <p className="mt-1 text-sm text-slate-600">
            {userIsAdmin
              ? ''
              : userIsAssistantManager
                ? 'First-level escalation handler. Resolve escalated items from staff/supervisors, or escalate to Admin if needed.'
                : isEntryStageUser
                  ? 'Create item change log entries and mark them as Resolved, Pending, or Escalated for manager review.'
                  : 'Log item issues manually and track escalations.'}
          </p>
        </div>
        <ExportButton onClick={exportExcel} />
      </div>

      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
        <div className="text-sm font-semibold text-slate-900 mb-3">Escalation Workflow</div>
        <div className="flex flex-col gap-2 text-xs text-slate-700 lg:flex-row lg:items-center lg:gap-4">
          <div className="flex items-center gap-2">
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 text-blue-700 font-semibold">1</div>
            <span><span className="font-semibold">Staff/Supervisor</span> creates entry</span>
          </div>
          <div className="hidden lg:inline text-slate-400">→</div>
          <div className="flex items-center gap-2">
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-100 text-amber-700 font-semibold">2</div>
            <span><span className="font-semibold">Assistant Manager</span> reviews & resolves</span>
          </div>
          <div className="hidden lg:inline text-slate-400">→</div>
          <div className="flex items-center gap-2">
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-rose-100 text-rose-700 font-semibold">3</div>
            <span><span className="font-semibold">Admin</span> final resolution</span>
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-4">
        <div className="card relative overflow-hidden border border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(148,163,184,0.18),_transparent_42%),linear-gradient(135deg,_#f8fafc_0%,_#ffffff_48%,_#f1f5f9_100%)] p-4">
          <div className="pointer-events-none absolute inset-0 opacity-40" style={{ backgroundImage: 'radial-gradient(rgba(148,163,184,0.28) 0.7px, transparent 0.7px)', backgroundSize: '12px 12px' }} />
          <div className="relative flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Total Items</p>
              <p className="mt-2 text-3xl font-black text-slate-900">{summary.total}</p>
            </div>
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 bg-white/80 shadow-sm">
              <Search className="h-5 w-5 text-slate-600" />
            </div>
          </div>
        </div>
        <div className="card relative overflow-hidden border border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(245,158,11,0.2),_transparent_42%),linear-gradient(135deg,_#fff7ed_0%,_#ffffff_48%,_#fffbeb_100%)] p-4">
          <div className="relative flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-700">Pending</p>
              <p className="mt-2 text-3xl font-black text-amber-700">{summary.pending}</p>
            </div>
            <AlertTriangle className="h-5 w-5 text-amber-600" />
          </div>
        </div>
        <div className="card relative overflow-hidden border border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(34,197,94,0.2),_transparent_42%),linear-gradient(135deg,_#ecfdf5_0%,_#ffffff_48%,_#f0fdf4_100%)] p-4">
          <div className="relative flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">Resolved</p>
              <p className="mt-2 text-3xl font-black text-emerald-700">{summary.resolved}</p>
            </div>
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
          </div>
        </div>
        <div className="card relative overflow-hidden border border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(244,63,94,0.2),_transparent_42%),linear-gradient(135deg,_#fff1f2_0%,_#ffffff_48%,_#fff1f2_100%)] p-4">
          <div className="relative flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-rose-700">Escalated</p>
              <p className="mt-2 text-3xl font-black text-rose-700">{summary.escalated}</p>
            </div>
            <Mail className="h-5 w-5 text-rose-600" />
          </div>
        </div>
      </div>

      <div className="rounded-[16px] border border-slate-200 bg-white p-5 shadow-[0_14px_40px_rgba(15,23,42,0.05)]">
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-end gap-3">
            <label className="relative block min-w-[220px] flex-[1.25] lg:max-w-[340px]">
              <span className="label">Search</span>
              <Search className="pointer-events-none absolute left-3 top-[2.55rem] h-4 w-4 text-gray-400" />
              <input
                className="input pl-10"
                placeholder="Order no, SKU, product, seller..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </label>

            <FilterSelect
              label="Status"
              value={statusFilter}
              options={statusOptions}
              onChange={(value) => setStatusFilter(String(value))}
              className="w-full sm:w-[160px]"
              disabled={loadingLookups}
            />

            <div className="w-full sm:w-[170px]">
              <label className="mb-1 block text-sm font-medium text-gray-700">Date From</label>
              <input
                className="input"
                type="date"
                value={dateFromFilter}
                onChange={(event) => setDateFromFilter(event.target.value)}
              />
            </div>
            <div className="w-full sm:w-[170px]">
              <label className="mb-1 block text-sm font-medium text-gray-700">Date To</label>
              <input
                className="input"
                type="date"
                value={dateToFilter}
                onChange={(event) => setDateToFilter(event.target.value)}
              />
            </div>

            {canResolveEscalations ? (
              <FilterSelect
                label="Manager Status"
                value={resolutionStatusFilter}
                options={resolutionOptions}
                onChange={(value) => setResolutionStatusFilter(String(value))}
                className="w-full sm:w-[180px]"
                disabled={loadingLookups}
              />
            ) : null}
          </div>

          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="text-sm text-slate-500">
              {isEntryStageUser ? (
                <>
                  <div>✓ Create entries and set status: <span className="font-semibold">Pending</span>, <span className="font-semibold">Resolved</span>, or <span className="font-semibold">Escalated</span></div>
                  <div className="mt-1 text-xs text-slate-400">Escalated items go to Assistant Manager for review</div>
                </>
              ) : userIsAssistantManager ? (
                <>
                  <div>✓ Review escalated items from staff/supervisors</div>
                  <div className="mt-1">Options: <span className="font-semibold">Resolve</span> or <span className="font-semibold">Escalate to Admin</span></div>
                  <div className="text-xs text-slate-400">Items escalated to Admin require final Admin resolution</div>
                </>
              ) : userIsAdmin ? (
                <>
                  <div>✓ Final resolution authority for items escalated by Assistant Managers</div>
                  <div className="mt-1"><span className="font-semibold">Resolve</span> all pending items</div>
                  <div className="text-xs text-slate-400">Only Admin can close escalations at this level</div>
                </>
              ) : (
                <>
                  Auto-fetched from order item audit history.
                </>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={toggleAllSelection}
                disabled={!filteredRows.length}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {selectedIds.length === filteredRows.length && filteredRows.length > 0 ? 'Clear Selection' : 'Select All'}
              </button>
              <button
                type="button"
                onClick={() => handleBulkUpdate('Resolved')}
                disabled={bulkSaving || selectedIds.length === 0 || isEntryStageUser}
                className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
                title={isEntryStageUser ? 'Only Managers can resolve items' : 'Mark selected items as Resolved'}
              >
                {bulkSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                {userIsAdmin ? 'Bulk Resolve (Final)' : 'Bulk Resolve'}
              </button>
              <button
                type="button"
                onClick={() => handleBulkUpdate('Escalated')}
                disabled={bulkSaving || selectedIds.length === 0 || userIsAdmin}
                className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-rose-500 disabled:cursor-not-allowed disabled:opacity-50"
                title={userIsAdmin ? 'Admin cannot escalate further' : userIsAssistantManager ? 'Escalate to Admin' : 'Mark items as requiring escalation'}
              >
                {bulkSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <AlertTriangle className="h-4 w-4" />}
                {userIsAssistantManager ? 'Escalate to Admin' : 'Bulk Escalate'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-[16px] border border-slate-200 bg-white p-5 shadow-[0_14px_40px_rgba(15,23,42,0.05)]">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Item Entries</h2>
            <p className="mt-1 text-sm text-slate-500">
              {canResolveEscalations
                ? 'Admin and Assistant Manager can see the later-stage follow-up section after the entry status is set.'
                : 'Each row stays on one line, uses dropdowns first, and only asks for manual entry when an option does not exist.'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-slate-500">
              {loading ? 'Refreshing rows...' : `${filteredRows.length} visible row${filteredRows.length === 1 ? '' : 's'}`}
            </span>
          </div>
        </div>

        <div className="mt-4 overflow-hidden rounded-[14px] border border-slate-200">
          {loading ? (
            <div className="bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
              Loading Item Change Log rows...
            </div>
          ) : null}

          {!loading && filteredRows.length === 0 ? (
            <div className="bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
              <p>No item entries match the current filters yet.</p>
            </div>
          ) : null}

          {!loading && filteredRows.length > 0 ? (
            <div className="table-scroll-shell">
              <table className="min-w-[1500px] border-collapse text-sm">
                <thead className="sticky top-0 z-20 bg-slate-100 shadow-[inset_0_-1px_0_0_#cbd5e1]">
                  <tr className="border-b border-slate-300 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-600">
                    <th className="px-3 py-3" colSpan="9">Entry Details</th>
                    <th className="px-3 py-3" colSpan="1">Issue</th>
                    <th className="px-3 py-3 border-l border-slate-300" colSpan="2" style={{ textAlign: 'center' }}>Staff/Supervisor</th>
                    {canViewAssistantManagerColumns && <th className="px-3 py-3 border-l border-slate-300" colSpan="2" style={{ textAlign: 'center' }}>Assistant Manager</th>}
                    {canViewAdminColumns && <th className="px-3 py-3 border-l border-slate-300" colSpan="2" style={{ textAlign: 'center' }}>Admin</th>}
                    <th className="px-3 py-3 border-l border-slate-300">Save</th>
                  </tr>
                  <tr className="border-b border-slate-200 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                    <th className="px-3 py-3">Select</th>
                    <th className="px-3 py-3">Order Number</th>
                    <th className="px-3 py-3">Changed Date Time</th>
                    <th className="px-3 py-3">Store</th>
                    <th className="px-3 py-3">Seller</th>
                    <th className="px-3 py-3">Product</th>
                    <th className="px-3 py-3">SKU</th>
                    <th className="px-3 py-3">Ordered Qty</th>
                    <th className="px-3 py-3">Delivered Qty</th>
                    <th className="px-3 py-3">Cause</th>
                    <th className="px-3 py-3 border-l border-slate-300">Action</th>
                    <th className="px-3 py-3">Status</th>
                    {canViewAssistantManagerColumns && (
                      <>
                        <th className="px-3 py-3 border-l border-slate-300">Action</th>
                        <th className="px-3 py-3">Status</th>
                      </>
                    )}
                    {canViewAdminColumns && (
                      <>
                        <th className="px-3 py-3 border-l border-slate-300">Action</th>
                        <th className="px-3 py-3">Status</th>
                      </>
                    )}
                    <th className="px-3 py-3 border-l border-slate-300">Save</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row) => {
                    const rowSelected = selectedIds.includes(row.id);
                    const rowResolved = row.status === 'Resolved' || row.resolutionStatus === 'Resolved';
                    const rowEscalated = row.status === 'Escalated' && row.resolutionStatus !== 'Resolved';
                    const statusTone = STATUS_TONE[row.status] || STATUS_TONE.Pending;
                    const resolutionTone = RESOLUTION_TONE[row.resolutionStatus] || RESOLUTION_TONE.Pending;
                    const causeDropdownOptions = buildDropdownOptions({
                      values: causeSuggestions,
                      currentValue: row.causeText,
                      placeholder: 'Select cause',
                      addLabel: 'Add new cause...',
                    });
                    const actionDropdownOptions = buildDropdownOptions({
                      values: actionSuggestions,
                      currentValue: row.actionText,
                      placeholder: 'Select action',
                      addLabel: 'Add new action...',
                    });
                    const managerActionDropdownOptions = buildDropdownOptions({
                      values: actionSuggestions,
                      currentValue: row.managerActionText,
                      placeholder: 'Select action',
                      addLabel: 'Add new action...',
                    });

                    return (
                      <tr
                        key={row.id}
                        className={`border-b border-slate-100 ${
                          rowSelected
                            ? 'bg-amber-50/70'
                            : rowResolved
                              ? 'bg-emerald-50/50'
                              : rowEscalated
                                ? 'bg-rose-50/50'
                                : 'bg-white'
                        }`}
                      >
                        <td className="px-3 py-3 align-top">
                          <input
                            type="checkbox"
                            checked={rowSelected}
                            onChange={() => toggleRowSelection(row.id)}
                            className="mt-2 h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                          />
                        </td>
                        <td className="px-3 py-3 align-top">
                          <div className="min-w-[150px]">
                            <div className="font-semibold text-slate-900">{row.orderNumber || '-'}</div>
                            {row.status === 'Escalated' ? (
                              <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600">
                                <Mail className="h-3.5 w-3.5" />
                                {row.escalationEmailStatus || 'not_required'}
                              </div>
                            ) : null}
                          </div>
                        </td>
                        <td className="px-3 py-3 align-top text-slate-700">
                          <div className="min-w-[170px]">{formatDateTime(row.changedDateTime)}</div>
                        </td>
                        <td className="px-3 py-3 align-top text-slate-700">
                          <div className="min-w-[140px]">{row.storeName || '-'}</div>
                        </td>
                        <td className="px-3 py-3 align-top text-slate-700">
                          <div className="min-w-[180px]">{row.sellerName || '-'}</div>
                        </td>
                        <td className="px-3 py-3 align-top text-slate-700">
                          <div className="min-w-[240px]">{row.productName || '-'}</div>
                        </td>
                        <td className="px-3 py-3 align-top text-slate-700">
                          <div className="min-w-[130px] font-medium">{row.sku || '-'}</div>
                        </td>
                        <td className="px-3 py-3 align-top text-slate-700">
                          <div className="min-w-[120px]">{row.orderedQty || '-'}</div>
                        </td>
                        <td className="px-3 py-3 align-top text-slate-700">
                          <div className="min-w-[120px]">{row.deliveredQty || '-'}</div>
                        </td>
                        <td className="px-3 py-3 align-top">
                          <FilterSelect
                            className="min-w-[220px]"
                            value={row.causeText || ''}
                            options={causeDropdownOptions}
                            onChange={(nextValue) => handleDropdownOrCustom(row.id, 'causeText', String(nextValue), 'Enter the new cause')}
                            disabled={!isEntryStageUser || row.status === 'Escalated'}
                            usePortal={false}
                          />
                          {!isEntryStageUser && <div className="mt-1 text-xs text-slate-400">Staff/Supervisor only</div>}
                        </td>
                        <td className="px-3 py-3 align-top border-l border-slate-300">
                          <FilterSelect
                            className="min-w-[220px]"
                            value={row.actionText || ''}
                            options={actionDropdownOptions}
                            onChange={(nextValue) => handleDropdownOrCustom(row.id, 'actionText', String(nextValue), 'Enter the new action')}
                            disabled={!isEntryStageUser || row.status === 'Escalated'}
                            usePortal={false}
                          />
                          <textarea
                            rows={4}
                            className={`${INLINE_SELECT_CLASS_NAME} mt-2 min-h-[110px] min-w-[280px] w-full resize-y`}
                            value={row.actionText || ''}
                            onChange={(event) => updateRowField(row.id, 'actionText', event.target.value)}
                            disabled={!isEntryStageUser || row.status === 'Escalated'}
                            placeholder="Explain Action Taken"
                          />
                          {!isEntryStageUser && <div className="mt-1 text-xs text-slate-400">Staff/Supervisor only</div>}
                        </td>
                        <td className="px-3 py-3 align-top">
                          <div className="space-y-2">
                            <FilterSelect
                              className="min-w-[180px]"
                              value={row.status || 'Pending'}
                              options={(options.status || []).map((option) => ({
                                value: option.label,
                                label: option.label,
                              }))}
                              onChange={(nextValue) => updateRowField(row.id, 'status', String(nextValue))}
                              disabled={!isEntryStageUser || row.status === 'Escalated'}
                              usePortal={false}
                            />
                            <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${statusTone}`}>
                              {row.status || 'Pending'}
                            </span>
                            <div className="text-[11px] text-slate-500">
                              Updated: {formatDateTime(row.updatedAt)}
                            </div>
                            {!isEntryStageUser && <div className="text-xs text-slate-400">Staff/Supervisor only</div>}
                          </div>
                        </td>
                        {canViewAssistantManagerColumns ? (
                          <>
                            <td className="px-3 py-3 align-top border-l border-slate-300">
                              <FilterSelect
                                className="min-w-[220px]"
                                value={row.managerActionText || row.assistantManagerActionText || ''}
                                options={managerActionDropdownOptions}
                                onChange={(nextValue) => handleDropdownOrCustom(row.id, 'managerActionText', String(nextValue), 'Enter the assistant manager action')}
                                disabled={!(userIsAssistantManager && row.status === 'Escalated')}
                                usePortal={false}
                              />
                              <textarea
                                rows={4}
                                className={`${INLINE_SELECT_CLASS_NAME} mt-2 min-h-[110px] min-w-[280px] w-full resize-y`}
                                value={row.managerActionText || row.assistantManagerActionText || ''}
                                onChange={(event) => updateRowField(row.id, 'managerActionText', event.target.value)}
                                disabled={!(userIsAssistantManager && row.status === 'Escalated')}
                                placeholder="Explain Action Taken"
                              />
                              <div className="mt-2 text-xs text-slate-500">
                                {!userIsAssistantManager ? (
                                  <span className="text-slate-400"></span>
                                ) : row.status !== 'Escalated' ? (
                                  <span className="text-slate-400">Awaiting staff escalation...</span>
                                ) : row.managerUpdatedByName ? (
                                  `Updated by ${row.managerUpdatedByName}`
                                ) : (
                                  'Ready for action'
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-3 align-top">
                              <div className="space-y-2">
                                <FilterSelect
                                  className="min-w-[180px]"
                                  value={row.resolutionStatus || 'Pending'}
                                  options={(options.resolution_status || options.status || []).map((option) => ({
                                    value: option.label,
                                    label: option.label,
                                  }))}
                                  onChange={(nextValue) => updateRowField(row.id, 'resolutionStatus', String(nextValue))}
                                  disabled={!(userIsAssistantManager && row.status === 'Escalated')}
                                  usePortal={false}
                                />
                                <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${resolutionTone}`}>
                                  {row.resolutionStatus || 'Pending'}
                                </span>
                                <div className="text-[11px] text-slate-500">
                                  Updated: {formatDateTime(row.managerUpdatedAt || row.updatedAt)}
                                </div>
                                {!userIsAssistantManager && <div className="text-xs text-slate-400"></div>}
                                {userIsAssistantManager && row.status !== 'Escalated' && (
                                  <div className="text-xs text-slate-400">Unlock when escalated</div>
                                )}
                              </div>
                            </td>
                          </>
                        ) : null}
                        {canViewAdminColumns ? (
                          <>
                            <td className="px-3 py-3 align-top border-l border-slate-300">
                              <FilterSelect
                                className="min-w-[220px]"
                                value={row.adminActionText || ''}
                                options={managerActionDropdownOptions}
                                onChange={(nextValue) => handleDropdownOrCustom(row.id, 'adminActionText', String(nextValue), 'Enter the admin action')}
                                disabled={!(userIsAdmin && row.resolutionStatus === 'Escalated')}
                                usePortal={false}
                              />
                              <textarea
                                rows={4}
                                className={`${INLINE_SELECT_CLASS_NAME} mt-2 min-h-[110px] min-w-[280px] w-full resize-y`}
                                value={row.adminActionText || ''}
                                onChange={(event) => updateRowField(row.id, 'adminActionText', event.target.value)}
                                disabled={!(userIsAdmin && row.resolutionStatus === 'Escalated')}
                                placeholder="Explain Action Taken"
                              />
                              <div className="mt-2 text-xs text-slate-500">
                                {row.resolutionStatus !== 'Escalated' ? (
                                  <span className="text-slate-400"></span>
                                ) : row.adminUpdatedByName ? (
                                  `Updated by ${row.adminUpdatedByName}`
                                ) : (
                                  'Ready for Admin action'
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-3 align-top">
                              <div className="space-y-2">
                                <FilterSelect
                                  className="min-w-[180px]"
                                  value={row.adminStatus || 'Pending'}
                                  options={(options.status || []).map((option) => ({
                                    value: option.label,
                                    label: option.label,
                                  }))}
                                  onChange={(nextValue) => updateRowField(row.id, 'adminStatus', String(nextValue))}
                                  disabled={!(userIsAdmin && row.resolutionStatus === 'Escalated')}
                                  usePortal={false}
                                />
                                <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                                  RESOLUTION_TONE[row.adminStatus] || RESOLUTION_TONE.Pending
                                }`}>
                                  {row.adminStatus || 'Pending'}
                                </span>
                                <div className="text-[11px] text-slate-500">
                                  Updated: {formatDateTime(row.updatedAt)}
                                </div>
                                {row.resolutionStatus !== 'Escalated' && (
                                  <div className="text-xs text-slate-400"></div>
                                )}
                              </div>
                            </td>
                          </>
                        ) : null}
                        <td className="px-3 py-3 align-top border-l border-slate-300">
                          <div className="min-w-[180px] text-xs text-slate-500">
                            {renderSaveState(row.id, row)}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default ItemChangeLog;
